"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.RangePicker = RangePicker;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const react_dom_1 = require("react-dom");
const cn_1 = require("../lib/cn");
const viewport_1 = require("../lib/viewport");
const overlay_1 = require("../lib/overlay");
const button_1 = require("./button");
const icon_1 = require("./icon");
const date_picker_1 = require("./date-picker");
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ A date range, with presets. Drives every report and timesheet period.      │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️ **The draft is separate from the value, and `Alkalmaz` is what commits it.** A range picker
 * that emits on every click fires once with a half-range — `from` set, `to` empty — and every
 * consumer downstream runs a query for it. The two-click selection makes an explicit apply
 * mandatory rather than stylistic.
 *
 * ⚠️ **`twoUp` is a prop, not `window.innerWidth >= 880` read during render.** The source
 * computes it inline, so it is decided once at first paint and never updates: a window resized
 * across the threshold keeps whichever layout it opened with, and under SSR it is always false.
 * A caller that wants it responsive owns a media query, where the subscription belongs.
 *
 * ⚠️ **The month grid comes from `monthCells`, shared with `DatePicker`.** The source's
 * `RangePicker` re-implements the calendar and its own Hungarian names, and gets both wrong: it
 * capitalises the months (`Január` — Hungarian does not) and abbreviates *szerda* and *szombat*
 * identically as `Sz`, leaving two columns of the week indistinguishable. One grid, one label
 * source, and the labels come from the caller.
 */
function RangePicker({ value, format, defaultRange, onChange, today, labels, twoUp: twoUpAsked = false, presets: presetsAsked = [], className, }) {
    /* ⚠️ 17 Oct 2026 · on a phone: ONE month, no presets, and the trigger is an icon — the product
       owner, for every range picker. Two months were wider than the screen. */
    const mobile = (0, viewport_1.useIsMobile)();
    const twoUp = twoUpAsked && !mobile;
    const presets = mobile ? [] : presetsAsked;
    const [open, setOpen] = (0, react_1.useState)(false);
    const [draft, setDraft] = (0, react_1.useState)(null);
    const [anchorMonth, setAnchorMonth] = (0, react_1.useState)(null);
    const dismiss = () => {
        setOpen(false);
        setDraft(null);
        setAnchorMonth(null);
    };
    const { surface, trigger } = (0, overlay_1.useDismiss)(open, dismiss);
    const { rect, measure } = (0, overlay_1.useAnchorRect)(open, trigger);
    const current = draft ?? { from: value?.from ?? '', to: value?.to ?? '' };
    const fallback = current.from === '' ? today : current.from;
    const cursor = anchorMonth ?? {
        year: Number(fallback.slice(0, 4)),
        month: Number(fallback.slice(5, 7)) - 1,
    };
    (0, react_1.useEffect)(() => {
        if (open)
            measure();
    }, [open, measure]);
    /**
     * Two clicks: the first starts a range, the second closes it. A second click BEFORE the first
     * swaps the ends rather than starting over — which is what somebody who clicked the wrong end
     * first means, and asking them to start again is the thing that makes range pickers annoying.
     *
     * ⚠️ String comparison is correct here and only because these are wire dates: `yyyy-mm-dd` is
     * lexicographically ordered. It is not correct for any other date format, which is one more
     * reason nothing but the wire form crosses this boundary.
     */
    const pick = (wire) => {
        if (current.from === '' || current.to !== '')
            setDraft({ from: wire, to: '' });
        else if (wire < current.from)
            setDraft({ from: wire, to: current.from });
        else
            setDraft({ from: current.from, to: wire });
    };
    const apply = () => {
        if (current.from === '')
            return;
        /* A single-day range is `from === to`, not `to: ''`. An empty `to` reaching a query means
         * "no upper bound", which is a very different report. */
        const range = { from: current.from, to: current.to === '' ? current.from : current.to };
        dismiss();
        onChange?.(range);
    };
    const reset = () => {
        const target = defaultRange ?? { from: '', to: '' };
        setDraft({ from: target.from, to: target.to });
        if (target.from !== '') {
            setAnchorMonth({
                year: Number(target.from.slice(0, 4)),
                month: Number(target.from.slice(5, 7)) - 1,
            });
        }
    };
    const step = (months) => {
        const moved = new Date(cursor.year, cursor.month + months, 1);
        setAnchorMonth({ year: moved.getFullYear(), month: moved.getMonth() });
    };
    const month = (offset) => {
        const at = new Date(cursor.year, cursor.month + offset, 1);
        const { lead, days } = (0, date_picker_1.monthCells)(at.getFullYear(), at.getMonth());
        return ((0, jsx_runtime_1.jsxs)("div", { className: "flex flex-1 flex-col gap-1", children: [(0, jsx_runtime_1.jsxs)("div", { className: "grid place-items-center text-[12.5px] font-medium", children: [at.getFullYear(), ". ", labels.months[at.getMonth()]] }), (0, jsx_runtime_1.jsxs)("div", { className: "grid grid-cols-7", children: [labels.weekdays.map((day) => ((0, jsx_runtime_1.jsx)("span", { className: "text-muted-foreground grid h-7 place-items-center text-[11px] font-medium", children: day }, day))), Array.from({ length: lead }, (_, index) => ((0, jsx_runtime_1.jsx)("span", {}, `lead-${String(index)}`))), days.map((wire) => {
                            const isEnd = wire === current.from || (current.to !== '' && wire === current.to);
                            const inside = current.from !== '' && current.to !== '' && wire > current.from && wire < current.to;
                            return ((0, jsx_runtime_1.jsx)("button", { type: "button", onClick: () => {
                                    pick(wire);
                                }, "aria-current": wire === today ? 'date' : undefined, "aria-pressed": isEnd || inside, className: (0, cn_1.cn)('grid h-8 place-items-center text-[12.5px] tabular-nums transition-colors', isEnd
                                    ? 'bg-primary text-primary-foreground font-medium'
                                    : inside
                                        ? 'bg-primary/15'
                                        : 'hover:bg-accent', isEnd && wire === current.from && 'rounded-l-md', isEnd && current.to !== '' && wire === current.to && 'rounded-r-md', !isEnd && !inside && 'rounded-md', wire === today && !isEnd && 'ring-ring/50 rounded-md ring-1 ring-inset'), children: Number(wire.slice(8)) }, wire));
                        })] })] }));
    };
    const width = (presets.length > 0 ? 168 : 0) + (twoUp ? 480 : 248) + 2;
    const popover = open && rect !== null
        ? (0, react_dom_1.createPortal)((0, jsx_runtime_1.jsxs)("div", { ref: surface, role: "dialog", "aria-label": labels.dialogLabel, style: (0, overlay_1.anchorTo)(rect, { width, align: 'end' }), className: "pop-in bg-popover text-popover-foreground shadow-pop flex overflow-hidden rounded-lg border", children: [presets.length > 0 ? ((0, jsx_runtime_1.jsx)("div", { className: "flex w-[168px] shrink-0 flex-col gap-0.5 border-r p-2", children: presets.map((preset) => {
                        const on = current.from === preset.from &&
                            (current.to === '' ? current.from : current.to) === preset.to;
                        return ((0, jsx_runtime_1.jsx)("button", { type: "button", "aria-pressed": on, onClick: () => {
                                setDraft({ from: preset.from, to: preset.to });
                                setAnchorMonth({
                                    year: Number(preset.from.slice(0, 4)),
                                    month: Number(preset.from.slice(5, 7)) - 1,
                                });
                            }, className: (0, cn_1.cn)('truncate rounded-md px-2 py-1.5 text-left text-[12.5px] transition-colors', on
                                ? 'bg-accent text-foreground font-medium'
                                : 'text-muted-foreground hover:bg-accent/60'), children: preset.label }, preset.label));
                    }) })) : null, (0, jsx_runtime_1.jsxs)("div", { className: "flex min-w-0 flex-1 flex-col gap-2 p-3", children: [(0, jsx_runtime_1.jsxs)("div", { className: "flex items-center gap-2", children: [(0, jsx_runtime_1.jsx)(button_1.Button, { variant: "ghost", size: "icon-sm", "aria-label": labels.previousMonth, onClick: () => {
                                        step(-1);
                                    }, children: (0, jsx_runtime_1.jsx)(icon_1.Icon, { name: "chevron-left", size: 15 }) }), (0, jsx_runtime_1.jsxs)("div", { className: "flex flex-1 gap-4", children: [month(0), twoUp ? month(1) : null] }), (0, jsx_runtime_1.jsx)(button_1.Button, { variant: "ghost", size: "icon-sm", "aria-label": labels.nextMonth, onClick: () => {
                                        step(1);
                                    }, children: (0, jsx_runtime_1.jsx)(icon_1.Icon, { name: "chevron-right", size: 15 }) })] }), (0, jsx_runtime_1.jsxs)("div", { className: "flex items-center gap-2 border-t pt-2", children: [(0, jsx_runtime_1.jsxs)("span", { className: "text-muted-foreground flex-1 truncate text-[11.5px] tabular-nums", children: [current.from === '' ? '–' : current.from, " \u2013", ' ', current.to === '' ? '–' : current.to] }), (0, jsx_runtime_1.jsx)(button_1.Button, { variant: "outline", size: "sm", onClick: reset, children: labels.reset }), (0, jsx_runtime_1.jsx)(button_1.Button, { size: "sm", onClick: apply, disabled: current.from === '', children: labels.apply })] })] })] }), document.body)
        : null;
    return ((0, jsx_runtime_1.jsxs)("div", { ref: trigger, className: (0, cn_1.cn)('relative shrink-0', className), children: [(0, jsx_runtime_1.jsxs)(button_1.Button, { variant: "outline", size: mobile ? 'icon-sm' : 'sm', onClick: () => {
                    if (open)
                        dismiss();
                    else
                        setOpen(true);
                }, "aria-haspopup": "dialog", "aria-expanded": open, "aria-label": labels.trigger, ...(mobile && format !== undefined && value != null && value.from !== ''
                    ? { title: format(value) }
                    : {}), children: [(0, jsx_runtime_1.jsx)(icon_1.Icon, { name: "calendar", size: 14 }), mobile ? null : ((0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [(0, jsx_runtime_1.jsxs)("span", { className: "inline-grid tabular-nums", children: [(0, jsx_runtime_1.jsx)("span", { className: "[grid-area:1/1]", children: format !== undefined && value != null && value.from !== '' && value.to !== ''
                                            ? format(value)
                                            : labels.trigger }), widest(format, value).map((text) => ((0, jsx_runtime_1.jsx)("span", { "aria-hidden": true, className: "invisible [grid-area:1/1]", children: text }, text)))] }), (0, jsx_runtime_1.jsx)(icon_1.Icon, { name: "chevron-down", size: 13, className: "opacity-60" })] }))] }), popover] }));
}
/**
 * ⚠️ 21 Oct 2026 · **ONE WIDTH, WHATEVER PERIOD IS SHOWING.** Stepping from *Sep 7 – Dec 27* to
 * *Sep 28, 2026 – Jan 17, 2027* widened the button and moved every control beside it, and a
 * single-digit day narrowed it again. The label shares its grid cell with invisible copies of the
 * widest labels this formatter can produce — every month, a two-digit day, across a year — so the
 * cell is always the widest of them, in whichever language the months are named.
 */
function widest(format, value) {
    if (format === undefined)
        return [];
    const year = Number((value?.from ?? '').slice(0, 4)) || new Date().getFullYear();
    const texts = new Set();
    for (let month = 1; month <= 12; month += 1) {
        const mm = String(month).padStart(2, '0');
        texts.add(format({ from: `${String(year)}-${mm}-28`, to: `${String(year + 1)}-${mm}-28` }));
    }
    return [...texts];
}
//# sourceMappingURL=range-picker.js.map